import {
  ISearchItemFieldsFragment,
  ISearchQuery,
  Maybe,
} from '../../../utils/apollo/graphqlSchema';
import {
  defaultSearchInput,
  universalSearchSupportedQueryTypes,
} from './utils';
import { ISearchInput, SearchableEntityTypes } from './types';
import SearchResultItem from './SearchResultItem/SearchResultItem';
import AdvancedSearchMenu from './AdvancedSearchMenu/AdvancedSearchMenu';
import UniversalSearchTabs from './UniversalSearchTabs';
import { useNavigate } from 'react-router-dom';
import { getClickRedirectUrl } from './SearchResultItem/utils';
import { useAppDispatch } from '../../../redux/hooks';
import {
  clearCreateNewReport,
  clearReplaceReport,
} from '../../../redux/slices/cachedEntities';
import { Loader } from '../../../components/Loaders';
import useCachedEntitiesSelector from '../../../redux/selectorHooks/useCachedEntitiesSelector';
import * as SearchMenuUI from './SearchMenuUI';

interface IProps {
  searchInput: ISearchInput;
  setSearchInput: (searchInput: ISearchInput, isLoadMore: boolean) => void;
  searchResult: Maybe<ISearchQuery['search']>;
  loading: boolean;
  handleMenuClose: () => void;
  isFavoriteOnly: boolean;
  toggleFavoriteOnly: (favoriteOnly: boolean) => void;
}

const SearchMenu = ({
  searchInput,
  setSearchInput,
  searchResult,
  loading,
  handleMenuClose,
  isFavoriteOnly,
  toggleFavoriteOnly,
}: IProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const createNewReport = useCachedEntitiesSelector('createNewReport');
  const replaceReport = useCachedEntitiesSelector('replaceReport');

  const onSetSearchType = (searchType: Maybe<SearchableEntityTypes>) => {
    setSearchInput(
      { ...searchInput, types: searchType ? [searchType] : [] },
      false,
    );
  };

  const loadMore = () => {
    setSearchInput(searchInput, true);
  };

  const handleEntityClick = (searchItem: ISearchItemFieldsFragment) => {
    navigate(getClickRedirectUrl(searchItem.entity));

    if (replaceReport) {
      dispatch(clearReplaceReport());
    }

    if (createNewReport) {
      dispatch(clearCreateNewReport());
    }

    handleMenuClose();
  };

  const handleResetClick = () => {
    setSearchInput(defaultSearchInput, false);
  };

  return (
    <SearchMenuUI.Container>
      <SearchMenuUI.ContentWrapper>
        <SearchMenuUI.StaticWrapper>
          <UniversalSearchTabs
            // Since searchInput.types is array, empty list represents ALL type.
            activeTab={searchInput.types?.[0] ?? null}
            onClick={onSetSearchType}
            isFavoriteOnly={isFavoriteOnly}
            toggleFavoriteOnly={toggleFavoriteOnly}
          />
        </SearchMenuUI.StaticWrapper>
        {loading ? (
          <Loader />
        ) : !searchResult || searchResult?.entities?.length === 0 ? (
          <SearchMenuUI.EmptyResult>No result found</SearchMenuUI.EmptyResult>
        ) : (
          <SearchMenuUI.StyledContent>
            {searchResult?.entities?.map((result, index) => {
              const key =
                result.entity.__typename === 'KeySearchEntity'
                  ? result.entity.name
                  : result.entity.id;
              return (
                <SearchResultItem
                  key={key + '-' + index}
                  searchItem={result}
                  onClick={() => handleEntityClick(result)}
                />
              );
            })}
            {searchResult?.pageInfo.hasMore && (
              <SearchMenuUI.LoadMoreButton
                size="default"
                onClick={() => loadMore()}
                trackContext="search"
                trackAction="universal - load more"
              >
                Load More
              </SearchMenuUI.LoadMoreButton>
            )}
          </SearchMenuUI.StyledContent>
        )}
      </SearchMenuUI.ContentWrapper>
      <AdvancedSearchMenu
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        supportedQueryTypes={universalSearchSupportedQueryTypes}
        onReset={handleResetClick}
      />
    </SearchMenuUI.Container>
  );
};

export default SearchMenu;
